'use client';
import MuiButton from '@mui/material/Button';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { white } from '../../colors';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import IconComponent from './IconComponent';

const defaultVariantProps = {
  primary: {
    color: 'primary',
    variant: 'contained',
  },
  secondary: {
    color: 'secondary',
    variant: 'outlined',
  },
  tertiary: {
    color: 'secondary',
    variant: 'contained',
  },
  text: {
    color: 'primary',
    variant: 'text',
  },
};

const fixVariant = (variant) => {
  return { variant, ...defaultVariantProps[variant] };
};

export const styles = ({ idsTokens = idsTokensDefault }) => ({
  darkMode: {
    color: white,
  },
  outlinedActive: {
    border: `1px solid ${idsTokens.button.default.color.secondary.border.enabled}`,
  },
  secondaryActive: {
    border: `1px solid ${idsTokens.button.default.color.secondary.border.enabled}`,
  },
  textActive: {
    backgroundColor: idsTokens.button.default.color.discreet.background.enabled,
  },
  primarySuccess: {
    backgroundColor: idsTokens.button.success.color.primary.background.enabled,
    border: `1px solid ${idsTokens.button.success.color.primary.border.enabled}`,
    color: idsTokens.button.success.color.primary.content.enabled,
    boxShadow: `${idsTokens.button.success.shadow.x} ${idsTokens.button.success.shadow.y} ${idsTokens.button.success.shadow.blur} ${idsTokens.button.success.shadow.spread} ${idsTokens.button.success.color.primary.shadow.enabled}`,
    paddingTop: idsTokens.button.success.spacing.standard.primary.padding.top,
    paddingRight: idsTokens.button.success.spacing.standard.primary.padding.right,
    paddingBottom: idsTokens.button.success.spacing.standard.primary.padding.bottom,
    paddingLeft: idsTokens.button.success.spacing.standard.primary.padding.left,
    minHeight: idsTokens.button.success.spacing.standard.primary.min.height,
    minWidth: idsTokens.button.success.spacing.standard.primary.min.width,
    gap: idsTokens.button.success.spacing.standard.primary.padding.gap,
    borderRadius: idsTokens.button.success.radius.standard.primary,
    '&:hover': {
      backgroundColor: idsTokens.button.success.color.primary.background.enabled,
      border: `1px solid ${idsTokens.button.success.color.primary.border.enabled}`,
      color: idsTokens.button.success.color.primary.content.enabled,
      boxShadow: `${idsTokens.button.success.shadow.x} ${idsTokens.button.success.shadow.y} ${idsTokens.button.success.shadow.blur} ${idsTokens.button.success.shadow.spread} ${idsTokens.button.success.color.primary.shadow.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.success.color.primary.background.hover, //#006611
        boxShadow: `${idsTokens.button.success.shadow.x} ${idsTokens.button.success.shadow.y} ${idsTokens.button.success.shadow.blur} ${idsTokens.button.success.shadow.spread} ${idsTokens.button.success.color.primary.shadow.hover}`,
        color: idsTokens.button.success.color.primary.content.hover,
        border: `1px solid ${idsTokens.button.success.color.primary.border.hover}`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.success.color.primary.background.disabled,
      color: idsTokens.button.success.color.primary.content.disabled,
      borderColor: idsTokens.button.success.color.primary.border.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.success.color.primary.background.focus,
      color: idsTokens.button.success.color.primary.content.focus,
    },
    '&:active': {
      color: idsTokens.button.success.color.primary.content.pressed,
      border: `1px solid ${idsTokens.button.success.color.primary.border.pressed}`,
      backgroundColor: idsTokens.button.success.color.primary.background.pressed,
      boxShadow: `${idsTokens.button.success.shadowPressed.x} ${idsTokens.button.success.shadowPressed.y} ${idsTokens.button.success.shadowPressed.blur} ${idsTokens.button.success.shadowPressed.spread} ${idsTokens.button.success.color.primary.shadow.pressed}`,
    },
  },
  secondarySuccess: {
    color: idsTokens.button.success.color.secondary.content.enabled,
    backgroundColor: idsTokens.button.success.color.secondary.background.enabled,
    border: `1px solid ${idsTokens.button.success.color.secondary.border.enabled}`,
    minHeight: idsTokens.button.success.spacing.standard.secondary.min.height,
    minWidth: idsTokens.button.success.spacing.standard.secondary.min.width,
    paddingTop: idsTokens.button.success.spacing.standard.secondary.padding.top,
    paddingRight: idsTokens.button.success.spacing.standard.secondary.padding.right,
    paddingBottom: idsTokens.button.success.spacing.standard.secondary.padding.bottom,
    paddingLeft: idsTokens.button.success.spacing.standard.secondary.padding.left,
    gap: idsTokens.button.success.spacing.standard.secondary.padding.gap,
    borderRadius: idsTokens.button.success.radius.standard.secondary,
    '&:hover': {
      backgroundColor: idsTokens.button.success.color.secondary.background.enabled,
      color: idsTokens.button.success.color.secondary.content.enabled,
      border: `1px solid ${idsTokens.button.success.color.secondary.border.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.success.color.secondary.background.hover,
        border: `1px solid ${idsTokens.button.success.color.secondary.border.hover}`,
        color: idsTokens.button.success.color.secondary.content.hover,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.success.color.secondary.background.disabled,
      color: idsTokens.button.success.color.secondary.content.disabled,
      borderColor: idsTokens.button.success.color.secondary.border.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      border: `1px solid ${idsTokens.button.success.color.secondary.border.focus}`,
      backgroundColor: idsTokens.button.success.color.secondary.background.focus,
      color: idsTokens.button.success.color.secondary.content.focus,
    },
    '&:active': {
      color: idsTokens.button.success.color.secondary.content.pressed,
      border: `1px solid ${idsTokens.button.success.color.secondary.border.pressed}`,
      backgroundColor: idsTokens.button.success.color.secondary.background.pressed,
    },
  },
  tertiarySuccess: {
    backgroundColor: idsTokens.button.success.color.tertiary.background.enabled,
    border: `1px solid ${idsTokens.button.success.color.tertiary.border.enabled}`,
    color: idsTokens.button.success.color.tertiary.content.enabled,
    boxShadow: 'none',
    paddingTop: idsTokens.button.success.spacing.standard.tertiary.padding.top,
    paddingRight: idsTokens.button.success.spacing.standard.tertiary.padding.right,
    paddingBottom: idsTokens.button.success.spacing.standard.tertiary.padding.bottom,
    paddingLeft: idsTokens.button.success.spacing.standard.tertiary.padding.left,
    minHeight: idsTokens.button.success.spacing.standard.tertiary.min.height,
    minWidth: idsTokens.button.success.spacing.standard.tertiary.min.width,
    gap: idsTokens.button.success.spacing.standard.tertiary.padding.gap,
    borderRadius: idsTokens.button.success.radius.standard.tertiary,
    '&:hover': {
      backgroundColor: idsTokens.button.success.color.tertiary.background.enabled,
      boxShadow: 'none',
      color: idsTokens.button.success.color.tertiary.content.enabled,
      border: `1px solid ${idsTokens.button.success.color.tertiary.border.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.success.color.tertiary.background.hover,
        boxShadow: 'none',
        color: idsTokens.button.success.color.tertiary.content.hover,
        border: `1px solid ${idsTokens.button.success.color.tertiary.border.hover}`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.success.color.tertiary.background.disabled,
      color: idsTokens.button.success.color.tertiary.content.disabled,
      borderColor: idsTokens.button.success.color.tertiary.border.disabled,
      boxShadow: 'none',
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.success.color.tertiary.background.focus,
      color: idsTokens.button.success.color.tertiary.content.focus,
      boxShadow: 'none',
    },
    '&:active': {
      color: idsTokens.button.success.color.tertiary.content.pressed,
      border: `1px solid ${idsTokens.button.success.color.tertiary.border.pressed}`,
      backgroundColor: idsTokens.button.success.color.tertiary.background.pressed,
      boxShadow: 'none',
    },
  },
  textSuccess: {
    paddingTop: idsTokens.button.success.spacing.standard.discreet.padding.top,
    paddingRight: idsTokens.button.success.spacing.standard.discreet.padding.right,
    paddingBottom: idsTokens.button.success.spacing.standard.discreet.padding.bottom,
    paddingLeft: idsTokens.button.success.spacing.standard.discreet.padding.left,
    gap: idsTokens.button.success.spacing.standard.discreet.padding.gap,
    color: idsTokens.button.success.color.discreet.content.enabled,
    minWidth: idsTokens.button.success.spacing.standard.discreet.min.width,
    minHeight: idsTokens.button.success.spacing.standard.discreet.min.height,
    borderRadius: idsTokens.button.success.radius.standard.discreet,
    backgroundColor: idsTokens.button.success.color.discreet.background.enabled,
    border: 'unset',
    '&:hover': {
      backgroundColor: idsTokens.button.success.color.discreet.background.enabled,
      color: idsTokens.button.success.color.discreet.content.enabled,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.success.color.discreet.background.hover,
        color: idsTokens.button.success.color.discreet.content.hover,
      },
    },
    '&:active': {
      background: idsTokens.button.success.color.discreet.background.pressed,
      color: idsTokens.button.success.color.discreet.content.pressed,
    },
    '&.Mui-disabled': {
      color: idsTokens.button.success.color.discreet.content.disabled,
      backgroundColor: idsTokens.button.success.color.discreet.background.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.success.color.discreet.background.focus,
      color: idsTokens.button.success.color.discreet.content.focus,
    },
  },
  primaryDanger: {
    backgroundColor: idsTokens.button.error.color.primary.background.enabled,
    border: `1px solid ${idsTokens.button.error.color.primary.border.enabled}`,
    color: idsTokens.button.error.color.primary.content.enabled,
    boxShadow: `${idsTokens.button.error.shadow.x} ${idsTokens.button.error.shadow.y} ${idsTokens.button.error.shadow.blur} ${idsTokens.button.error.shadow.spread} ${idsTokens.button.error.color.primary.shadow.enabled}`,
    paddingTop: idsTokens.button.error.spacing.standard.primary.padding.top,
    paddingRight: idsTokens.button.error.spacing.standard.primary.padding.right,
    paddingBottom: idsTokens.button.error.spacing.standard.primary.padding.bottom,
    paddingLeft: idsTokens.button.error.spacing.standard.primary.padding.left,
    minHeight: idsTokens.button.error.spacing.standard.primary.min.height,
    minWidth: idsTokens.button.error.spacing.standard.primary.min.width,
    gap: idsTokens.button.error.spacing.standard.primary.padding.gap,
    borderRadius: idsTokens.button.error.radius.standard.primary,
    '&:hover': {
      backgroundColor: idsTokens.button.error.color.primary.background.enabled,
      boxShadow: `${idsTokens.button.error.shadow.x} ${idsTokens.button.error.shadow.y} ${idsTokens.button.error.shadow.blur} ${idsTokens.button.error.shadow.spread} ${idsTokens.button.error.color.primary.shadow.enabled}`,
      color: idsTokens.button.error.color.primary.content.enabled,
      border: `1px solid ${idsTokens.button.error.color.primary.border.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.error.color.primary.background.hover,
        boxShadow: `${idsTokens.button.error.shadow.x} ${idsTokens.button.error.shadow.y} ${idsTokens.button.error.shadow.blur} ${idsTokens.button.error.shadow.spread} ${idsTokens.button.error.color.primary.shadow.hover}`,
        color: idsTokens.button.error.color.primary.content.hover,
        border: `1px solid ${idsTokens.button.error.color.primary.border.hover}`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.error.color.primary.background.disabled,
      color: idsTokens.button.error.color.primary.content.disabled,
      borderColor: idsTokens.button.error.color.primary.border.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.error.color.primary.background.focus,
      color: idsTokens.button.error.color.primary.content.focus,
    },
    '&:active': {
      color: idsTokens.button.error.color.primary.content.pressed,
      border: `1px solid ${idsTokens.button.error.color.primary.border.pressed}`,
      backgroundColor: idsTokens.button.error.color.primary.background.pressed,
      boxShadow: `${idsTokens.button.error.shadowPressed.x} ${idsTokens.button.error.shadowPressed.y} ${idsTokens.button.error.shadowPressed.blur} ${idsTokens.button.error.shadowPressed.spread} ${idsTokens.button.error.color.primary.shadow.pressed}`,
    },
  },
  secondaryDanger: {
    color: idsTokens.button.error.color.secondary.content.enabled,
    backgroundColor: idsTokens.button.error.color.secondary.background.enabled,
    border: `1px solid ${idsTokens.button.error.color.secondary.border.enabled}`,
    minHeight: idsTokens.button.error.spacing.standard.secondary.min.height,
    minWidth: idsTokens.button.error.spacing.standard.secondary.min.width,
    paddingTop: idsTokens.button.error.spacing.standard.secondary.padding.top,
    paddingRight: idsTokens.button.error.spacing.standard.secondary.padding.right,
    paddingBottom: idsTokens.button.error.spacing.standard.secondary.padding.bottom,
    paddingLeft: idsTokens.button.error.spacing.standard.secondary.padding.left,
    gap: idsTokens.button.error.spacing.standard.secondary.padding.gap,
    borderRadius: idsTokens.button.error.radius.standard.secondary,
    '&:hover': {
      backgroundColor: idsTokens.button.error.color.secondary.background.enabled,
      border: `1px solid ${idsTokens.button.error.color.secondary.border.enabled}`,
      color: idsTokens.button.error.color.secondary.content.enabled,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.error.color.secondary.background.hover,
        border: `1px solid ${idsTokens.button.error.color.secondary.border.hover}`,
        color: idsTokens.button.error.color.secondary.content.hover,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.error.color.secondary.background.disabled,
      color: idsTokens.button.error.color.secondary.content.disabled,
      borderColor: idsTokens.button.error.color.secondary.border.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      border: `1px solid ${idsTokens.button.error.color.secondary.border.focus}`,
      color: idsTokens.button.error.color.secondary.content.focus,
    },
    '&:active': {
      color: idsTokens.button.error.color.secondary.content.pressed,
      border: `1px solid ${idsTokens.button.error.color.secondary.border.pressed}`,
      backgroundColor: idsTokens.button.error.color.secondary.background.pressed,
    },
  },
  tertiaryDanger: {
    backgroundColor: idsTokens.button.error.color.tertiary.background.enabled,
    border: `1px solid ${idsTokens.button.error.color.tertiary.border.enabled}`,
    color: idsTokens.button.error.color.tertiary.content.enabled,
    boxShadow: 'none',
    paddingTop: idsTokens.button.error.spacing.standard.tertiary.padding.top,
    paddingRight: idsTokens.button.error.spacing.standard.tertiary.padding.right,
    paddingBottom: idsTokens.button.error.spacing.standard.tertiary.padding.bottom,
    paddingLeft: idsTokens.button.error.spacing.standard.tertiary.padding.left,
    minHeight: idsTokens.button.error.spacing.standard.tertiary.min.height,
    minWidth: idsTokens.button.error.spacing.standard.tertiary.min.width,
    gap: idsTokens.button.error.spacing.standard.tertiary.padding.gap,
    borderRadius: idsTokens.button.error.radius.standard.tertiary,
    '&:hover': {
      backgroundColor: idsTokens.button.error.color.tertiary.background.enabled,
      boxShadow: 'none',
      color: idsTokens.button.error.color.tertiary.content.enabled,
      border: `1px solid ${idsTokens.button.error.color.tertiary.border.enabled}`,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.error.color.tertiary.background.hover,
        boxShadow: 'none',
        color: idsTokens.button.error.color.tertiary.content.hover,
        border: `1px solid ${idsTokens.button.error.color.tertiary.border.hover}`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: idsTokens.button.error.color.tertiary.background.disabled,
      color: idsTokens.button.error.color.tertiary.content.disabled,
      borderColor: idsTokens.button.error.color.tertiary.border.disabled,
      opacity: 1,
      boxShadow: 'none',
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.error.color.tertiary.background.focus,
      color: idsTokens.button.error.color.tertiary.content.focus,
      boxShadow: 'none',
    },
    '&:active': {
      color: idsTokens.button.error.color.tertiary.content.pressed,
      border: `1px solid ${idsTokens.button.error.color.tertiary.border.pressed}`,
      backgroundColor: idsTokens.button.error.color.tertiary.background.pressed,
      boxShadow: 'none',
    },
  },
  textDanger: {
    paddingTop: idsTokens.button.error.spacing.standard.discreet.padding.top,
    paddingRight: idsTokens.button.error.spacing.standard.discreet.padding.right,
    paddingBottom: idsTokens.button.error.spacing.standard.discreet.padding.bottom,
    paddingLeft: idsTokens.button.error.spacing.standard.discreet.padding.left,
    gap: idsTokens.button.error.spacing.standard.discreet.padding.gap,
    color: idsTokens.button.error.color.discreet.content.enabled,
    minWidth: idsTokens.button.error.spacing.standard.discreet.min.width,
    minHeight: idsTokens.button.error.spacing.standard.discreet.min.height,
    borderRadius: idsTokens.button.error.radius.standard.discreet,
    backgroundColor: idsTokens.button.error.color.discreet.background.enabled,
    border: 'unset',
    '&:hover': {
      backgroundColor: idsTokens.button.error.color.discreet.background.enabled,
      color: idsTokens.button.error.color.discreet.content.enabled,
    },
    '@media (hover:hover)': {
      '&:hover': {
        backgroundColor: idsTokens.button.error.color.discreet.background.hover,
        color: idsTokens.button.error.color.discreet.content.hover,
      },
    },
    '&:active': {
      background: idsTokens.button.error.color.discreet.background.pressed,
      color: idsTokens.button.error.color.discreet.content.pressed,
    },
    '&.Mui-disabled': {
      color: idsTokens.button.error.color.discreet.content.disabled,
      backgroundColor: idsTokens.button.error.color.discreet.background.disabled,
      opacity: 1,
    },
    '&:focus-visible': {
      backgroundColor: idsTokens.button.error.color.discreet.background.focus,
      color: idsTokens.button.error.color.discreet.content.focus,
    },
  },
});

const useStyles = makeStyles()(styles);

export const Button = ({
  darkMode,
  children,
  icon,
  size = 'medium',
  variant = 'text',
  className,
  open,
  status,
  iconPosition = 'start',
  forwardedRef: ref,
  ...rest
}) => {
  const { classes, cx } = useStyles();

  return (
    <MuiButton
      disableRipple
      {...fixVariant(variant)}
      size={size}
      className={cx(
        darkMode && classes.darkMode,
        open && classes[`${variant}Active`],
        status === 'success' && classes[`${variant}Success`],
        status === 'danger' && classes[`${variant}Danger`],
        className
      )}
      {...rest}
      ref={ref}
    >
      {icon && iconPosition === 'start' && <IconComponent key="icon" size={size} icon={icon} />}
      {children}
      {icon && iconPosition === 'end' && <IconComponent key="icon" size={size} icon={icon} />}
    </MuiButton>
  );
};

Button.propTypes = {
  /** The content of the button. */
  children: PropTypes.node,
  /** If `true`, the component has a lighter appearance, for display on a dark background. */
  darkMode: PropTypes.bool,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the component takes up the full width of its container. */
  fullWidth: PropTypes.bool,
  /**
   * The URL to link to when the button is clicked. If defined, an `a` element
   * will be used as the root node.
   */
  href: PropTypes.string,
  /** Add an icon to the left of the button text. */
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  /** The position of the icon within the button. */
  iconPosition: PropTypes.oneOf(['start', 'end']),
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** If `true`, the button is displayed in an active state.
   * @ignore */
  open: PropTypes.bool,
  /** The size of the component. */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Coloring of the button based on a status. */
  status: PropTypes.oneOf(['danger', 'success']),
  /** The variant to use. */
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'text']),
};

export default withRef()(Button);
